import { useEffect, useRef, useState } from "react";
import { CheckCheck, Send, Sparkles } from "lucide-react";
import { Input } from "@/shared/ui/input.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Spinner } from "@/shared/ui/spinner.jsx";
import { formatMessageTime } from "@/shared/lib/format.js";
import { useLeadMessages, useSendMessage } from "../use-leads.js";

/**
 * Aba de conversa do lead.
 *
 * @param {{ leadId: string }} props
 */
export function LeadChatTab({ leadId }) {
  const { data: mensagens = [], isLoading } = useLeadMessages(leadId);
  const enviar = useSendMessage();
  const [rascunho, setRascunho] = useState("");
  const fimDaLista = useRef(null);

  // Rola para a mensagem mais recente sempre que a conversa muda.
  useEffect(() => {
    fimDaLista.current?.scrollIntoView({ block: "end" });
  }, [mensagens.length, leadId]);

  function enviarMensagem() {
    const texto = rascunho.trim();
    if (!texto) return;
    setRascunho("");
    enviar.mutate(
      { leadId, text: texto },
      { onError: () => setRascunho(texto) }, // devolve o texto se falhar
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="scrollbar-slim flex-1 space-y-3 overflow-y-auto bg-[radial-gradient(circle_at_top,_var(--color-muted)_0,_transparent_50%)] p-5">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner className="h-6 w-6 text-muted-foreground" />
          </div>
        ) : mensagens.length === 0 ? (
          <p className="text-center text-xs text-muted-foreground">
            Nenhuma conversa ainda. Envie a primeira mensagem.
          </p>
        ) : (
          mensagens.map((m) => <MessageBubble key={m.id} message={m} />)
        )}
        <div ref={fimDaLista} />
      </div>

      <div className="border-t border-border/60 bg-card/30 p-3">
        <div className="flex items-end gap-2 rounded-2xl border border-border bg-background p-2">
          <Input
            value={rascunho}
            onChange={(e) => setRascunho(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && enviarMensagem()}
            placeholder="Mensagem para o WhatsApp..."
            maxLength={1000}
            aria-label="Mensagem"
            className="h-9 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
          />
          <Button
            onClick={enviarMensagem}
            size="icon"
            className="h-8 w-8"
            disabled={enviar.isPending || !rascunho.trim()}
            aria-label="Enviar mensagem"
          >
            {enviar.isPending ? <Spinner /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Balão de mensagem. Exportado porque a inbox do WhatsApp mostra as mesmas
 * mensagens com a mesma aparência.
 */
export function MessageBubble({ message }) {
  const minha = message.from !== "lead";

  return (
    <div className={`flex ${minha ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${
          minha
            ? "rounded-br-sm bg-primary text-primary-foreground"
            : "rounded-bl-sm border border-border/60 bg-card"
        }`}
      >
        {message.from === "bot" && (
          <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider opacity-70">
            <Sparkles className="h-2.5 w-2.5" />
            Automação
          </p>
        )}
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.text}</p>
        <div
          className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${
            minha ? "text-primary-foreground/70" : "text-muted-foreground"
          }`}
        >
          {formatMessageTime(message.createdAt)}
          {minha && <CheckCheck className="h-3 w-3" />}
        </div>
      </div>
    </div>
  );
}
